import React, { useState, useEffect, useCallback } from 'react';
import {
  ModalOverlay,
  ModalWrapper,
  ModalContent,
  CloseButton,
  QRCodeImage,
  TransactionDetails,
  ButtonPaymentModal,
} from '../styles';

const PaymentModal = ({ modalOpen, closeModal, paymentData, copyToClipboard, initialCountdown }) => {
  const [countdown, setCountdown] = useState(initialCountdown);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [paymentStatusMessage, setPaymentStatusMessage] = useState('');
  const [transactionId, setTransactionId] = useState(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [closingTimer, setClosingTimer] = useState(null);

  // Função para fechar o modal, memoizada para evitar redefinições
  const handleCloseModal = useCallback(() => {
    closeModal();
  }, [closeModal]);

  // Efeito para configurar o WebSocket
  useEffect(() => {
    if (modalOpen && paymentData) {
      const newSocket = new WebSocket('wss://api.bluehousetennis.com.br/ws');
      newSocket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.transactionID === paymentData.transactionID) {
            if (message.status === "CONFIRMED") {
              setPaymentStatusMessage("Pagamento Efetuado com Sucesso. Agendamento realizado.");
              setTransactionId(message.transactionID);
              setCountdown(null);
              setPaymentConfirmed(true);
              setShowConfirmation(false);

              // Fechar o modal após 1 minuto
              const timer = setTimeout(() => {
                handleCloseModal();
              }, 60000);
              setClosingTimer(timer);
            }
          }
        } catch (error) {
          console.error('Erro ao analisar JSON:', error);
        }
      };

      return () => {
        if (newSocket) newSocket.close();
        if (closingTimer) clearTimeout(closingTimer);
      };
    }
  }, [modalOpen, paymentData, closingTimer, handleCloseModal]);

  // Efeito para contar o tempo restante
  useEffect(() => {
    if (modalOpen) {
      // Reinicia o contador toda vez que o modal é aberto
      setCountdown(initialCountdown); // 5 minutos

      if (!paymentConfirmed) {
        // Inicia o contador
        const interval = setInterval(() => {
          setCountdown((prevCountdown) => {
            if (prevCountdown <= 1) {
              clearInterval(interval); // Limpa o intervalo quando o tempo acaba
              return 0; // Define o contador como 0
            }
            return prevCountdown - 1;
          });
        }, 1000);

        // Limpa o intervalo ao desmontar ou ao fechar o modal
        return () => clearInterval(interval);
      }
    } else {
      // Garante que o contador seja parado ao fechar o modal
      setCountdown(initialCountdown); // Reinicia o contador quando o modal é fechado
    }
  }, [modalOpen, paymentConfirmed, initialCountdown]);

  const formatCountdown = (seconds) => {
    const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${minutes}:${secs}`;
  };

  // Função para lidar com o fechamento do modal com confirmação
  const handleManualClose = () => {

    if (!paymentConfirmed && countdown > 0) {
      setShowConfirmation(true); // Define para exibir a mensagem
      return; // Interrompe o fechamento imediato
    }

    // Fecha diretamente o modal se o pagamento já foi confirmado ou o countdown expirou
    closeModal();
  };

  return (
    modalOpen && (
      <ModalOverlay>
        <ModalWrapper>
          <ModalContent>
            <CloseButton onClick={handleManualClose}>✖</CloseButton>
            {paymentConfirmed ? (
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '18px', fontWeight: 'bold', color: 'green' }}>
                  {paymentStatusMessage}
                </p>
                {transactionId && (
                  <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#2A7BBF' }}>
                    Transaction ID: {transactionId}
                  </p>
                )}
              </div>
            ) : (
              <>
                <p style={{ marginTop: '25px', fontSize: '18px', fontWeight: 'bold' }}>
                  Escaneie o QRCode ou clique no botão abaixo para realizar o pagamento.
                </p>

                {countdown > 0 && (
                  <p style={{ fontSize: '16px', color: '#2A7BBF', fontWeight: 'bold', marginTop: '10px' }}>
                    Tempo restante: {formatCountdown(countdown)}
                  </p>
                )}

                {paymentData && (
                  <div style={{ marginTop: '20px' }}>
                    <QRCodeImage
                      src={paymentData.qrCodeImage}
                      alt="QR Code"
                    />
                    
                    <div>
                      <strong>
                        Agendamento para {paymentData.dates[0].type === 'unique' ? 'o dia:' : 'os dias:'}
                      </strong>

                      {paymentData.dates.map((dateObj, index) => {
                        const [year, month, day] = dateObj.date.split('-');
                        const formattedDate = `${day}-${month}-${year}`; // Ajusta o formato diretamente

                        return (
                          <div key={index}>
                            <strong>{formattedDate}</strong>
                          </div>
                        );
                      })}
                      <strong>
                        Horário: {paymentData.dates[0].start_time} às {paymentData.dates[0].end_time}
                      </strong>
                    </div>
                    
                    <TransactionDetails>Valor total: {paymentData.totalValue}</TransactionDetails>

                    <ButtonPaymentModal onClick={() => copyToClipboard(paymentData.brCode)}>
                      Pix Copia e Cola
                    </ButtonPaymentModal>
                  </div>
                )}
              </>
            )}
          </ModalContent>
        </ModalWrapper>       
        {showConfirmation && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000, // Garante que fique acima do modal
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "8px",
                maxWidth: "400px",
                textAlign: "center",
              }}
            >
              <p>
                O pagamento via PIX ainda está pendente. Tem certeza de que deseja
                fechar?
              </p>
              <button
                onClick={() => {
                  setShowConfirmation(false); // Fechar mensagem
                  closeModal(); // Fechar modal
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "red",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  marginRight: "10px",
                  cursor: "pointer",
                }}
              >
                Sim, fechar
              </button>
              <button
                onClick={() => setShowConfirmation(false)} // Apenas fecha a mensagem
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#2A7BBF",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Não, continuar
              </button>
            </div>
          </div>
        )}
      </ModalOverlay>
    )
  );
};

export default PaymentModal;
